import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLock, faTriangleExclamation, faHourglassHalf, faArrowRight, faDiagramProject
} from '@fortawesome/free-solid-svg-icons';

import portalApi, {
    getPortalToken, clearPortalToken, getGuestSession, setGuestSession
} from '../utils/portalApi';
import '../styles/messenger.css';
import '../styles/portal.css';

/**
 * Where an invitation link lands - feature draft Module 2, and the front door
 * of the guest role.
 *
 * Rendered outside ProtectedRoute and DashboardLayout, so there is no staff
 * sidebar or topbar. This page only accepts the invitation: once somebody is
 * in, they are a guest with a session of their own, and everything they can
 * use - this group, any others, their meetings, their profile - is on their
 * guest home (pages/GuestHome.js).
 *
 * No password is asked for here. Possession of the emailed link is the credential; the
 * optional verification code is the only thing between the click and the
 * thread, and only when the inviter asked for it.
 */

/** A full-page message for every state that is not "come in". */
const Notice = ({ icon, title, children, tone = '' }) => (
    <div className="portal-shell">
        <div className={`portal-card notice ${tone}`}>
            <div className="portal-notice-icon"><FontAwesomeIcon icon={icon} /></div>
            <h2>{title}</h2>
            {children}
        </div>
    </div>
);

const Portal = () => {
    const { token: inviteToken } = useParams();
    const navigate = useNavigate();
    const activeInviteRef = useRef(inviteToken);
    activeInviteRef.current = inviteToken;

    // loading | gate | pending | blocked
    const [phase, setPhase] = useState('loading');
    const [invite, setInvite] = useState(null);
    const [blocked, setBlocked] = useState({ title: '', detail: '' });

    const [code, setCode] = useState('');
    const [joining, setJoining] = useState(false);
    const [codeError, setCodeError] = useState('');

    const enter = (session, conversationId) => {
        setGuestSession(session);
        navigate(`/guest/chats/${conversationId}`, { replace: true });
    };

    useEffect(() => {
        let cancelled = false;
        setPhase('loading');
        setCode('');
        setCodeError('');
        setJoining(false);

        (async () => {
            /*
             * A session from before guest sign-in existed is traded for the
             * guest's own, so somebody who joined earlier is not asked for
             * their code again.
             */
            const legacy = getPortalToken(inviteToken);
            if (legacy) {
                try {
                    const { data } = await portalApi.post('/session', {}, { headers: { 'x-portal-token': legacy } });
                    clearPortalToken(inviteToken);
                    if (!cancelled) enter(data.session, data.conversationId);
                    return;
                } catch {
                    clearPortalToken(inviteToken);
                }
            }

            try {
                const guest = getGuestSession();
                const res = await portalApi.get(`/invite/${inviteToken}`,
                    guest ? { headers: { 'x-portal-token': guest } } : undefined);
                if (cancelled) return;
                setInvite(res.data);

                if (res.data.revoked) {
                    setBlocked({
                        title: 'This invitation has been withdrawn',
                        detail: 'Your contact at the company can send you a new one if you still need access.'
                    });
                    setPhase('blocked');
                } else if (res.data.conversationId) {
                    // Already joined, and signed in as the guest it belongs to.
                    navigate(`/guest/chats/${res.data.conversationId}`, { replace: true });
                } else if (res.data.expired) {
                    setBlocked({
                        title: 'This invitation has expired',
                        detail: 'Invitations must be accepted within a set time. Ask your contact to send a fresh one.'
                    });
                    setPhase('blocked');
                } else if (res.data.status === 'pending') {
                    setPhase('pending');
                } else {
                    setPhase('gate');
                }
            } catch (err) {
                if (cancelled) return;
                setBlocked({
                    title: 'This link is not valid',
                    detail: err.response?.data?.message || 'Check that you copied the whole link from your email.'
                });
                setPhase('blocked');
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteToken]);

    const join = async () => {
        setJoining(true);
        setCodeError('');
        try {
            const res = await portalApi.post(`/invite/${inviteToken}/join`,
                invite?.requiresCode ? { code: code.trim() } : {});
            if (activeInviteRef.current !== inviteToken) return;

            // 202 - an approval-gated invitation (F2.2). No session yet.
            if (res.status === 202) {
                setPhase('pending');
                return;
            }
            enter(res.data.session, res.data.conversation._id);
        } catch (err) {
            if (activeInviteRef.current !== inviteToken) return;
            const status = err.response?.status;
            const message = err.response?.data?.message || 'Could not open the conversation.';
            if (status === 401 || status === 429) {
                setCodeError(message);
            } else {
                setBlocked({ title: 'Cannot open this conversation', detail: message });
                setPhase('blocked');
            }
        } finally {
            if (activeInviteRef.current === inviteToken) setJoining(false);
        }
    };

    if (phase === 'loading') {
        return (
            <div className="portal-shell">
                <div className="portal-card notice">
                    <div className="portal-spinner" />
                    <p>Opening your invitation…</p>
                </div>
            </div>
        );
    }

    if (phase === 'blocked') {
        return (
            <Notice icon={faTriangleExclamation} title={blocked.title} tone="warn">
                <p>{blocked.detail}</p>
                {getGuestSession() && (
                    <button className="portal-primary" onClick={() => navigate('/guest')}>
                        Go to your guest home <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                )}
            </Notice>
        );
    }

    if (phase === 'pending') {
        return (
            <Notice icon={faHourglassHalf} title="Waiting for approval">
                <p>
                    Your request to join
                    {invite?.groupName ? <strong> {invite.groupName}</strong> : ' this conversation'}
                    {' '}has been sent to {invite?.invitedByName || 'your contact'}.
                </p>
                <p className="portal-muted">
                    You will be able to open this link once it is approved. Nothing else is
                    needed from you - keep the link and try again shortly.
                </p>
            </Notice>
        );
    }

    return (
        <div className="portal-shell">
            <div className="portal-card">
                <div className="portal-brand">GTS</div>
                <p className="portal-eyebrow">You have been invited</p>
                <h2>
                    {invite.invitedByName} invited you to
                    <span className="portal-group">{invite.groupName}</span>
                </h2>
                {invite.projectName && (
                    <span className="portal-chip">
                        <FontAwesomeIcon icon={faDiagramProject} />
                        {invite.projectName}
                    </span>
                )}

                <p className="portal-intro">
                    Hello {invite.name}. Accepting this invitation gives you guest access to
                    this conversation with the team. Next time, sign in with your email at
                    the guest sign-in page with an emailed code, or set a password from your profile.
                </p>

                {invite.requiresCode && (
                    <>
                        <label className="portal-label">
                            Enter the six-digit code from your invitation email
                        </label>
                        <input
                            className="portal-code"
                            value={code}
                            inputMode="numeric"
                            maxLength={6}
                            autoFocus
                            placeholder="000000"
                            onChange={(e) => {
                                setCode(e.target.value.replace(/\D/g, ''));
                                setCodeError('');
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') join(); }}
                        />
                    </>
                )}

                {codeError && <p className="portal-error">{codeError}</p>}

                <button
                    className="portal-primary"
                    onClick={join}
                    disabled={joining || (invite.requiresCode && code.length !== 6)}
                >
                    {joining ? 'Opening…' : (
                        <>
                            {invite.requiresApproval ? 'Request access' : 'Accept and open'}
                            <FontAwesomeIcon icon={faArrowRight} />
                        </>
                    )}
                </button>

                <p className="portal-foot">
                    <FontAwesomeIcon icon={faLock} />
                    You will only see the conversations and meetings you are invited to.
                </p>
            </div>
        </div>
    );
};

export default Portal;
